/* eslint-disable @typescript-eslint/no-explicit-any */
// Imports for external libraries go here.
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { StyledSelectedFiltersBar } from './SelectedFiltersBar.styles';
import { selectedFiltersBarProps } from './SelectedFiltersBar.types';
import { Icon } from '@marriott/mi-ui-library';
import { DisplayedFilter } from '../SearchResultsComponent.types';
import { DEFAULT_PLUS_WIDTH, SPACING } from '../../../modules/constants/constant';
import { debounce } from '../../../modules/helpers';

export const SelectedFiltersBar: FC<selectedFiltersBarProps> = ({
  selectedFilters,
  onRemoveFilter,
  popupOpenState,
  priorityList,
}) => {
  //To get the available container width
  const containerRef = useRef<HTMLDivElement>(null);

  /**
   * Too measure and calculate the hidden count....
   */
  const pillsRef = useRef<HTMLDivElement>(null);

  const hiddenCountRef = useRef<HTMLDivElement>(null);

  const [hiddenCount, setHiddenCount] = useState(0);

  const sortedFilters = useMemo(() => {
    return [...selectedFilters].sort((a, b) => priorityList[a.type] - priorityList[b.type]);
  }, [selectedFilters, priorityList]);

  useEffect(() => {
    const container = containerRef.current;
    const pillsCont = pillsRef.current;
    if (!pillsCont || !container) return;

    const calculateHiddenCount = () => {
      const dynamicPlusWidth = hiddenCountRef?.current?.offsetWidth || DEFAULT_PLUS_WIDTH;

      //get's the available container width
      const availableWidth = container.clientWidth;

      /**
       * Selected pills .....
       */
      const buttons = Array.from(pillsCont.children) as HTMLElement[];

      let usedWidth = 0;
      let visibleCount = 0;

      for (const button of buttons) {
        const btnWidth = button.offsetWidth + SPACING;
        if (usedWidth + btnWidth + dynamicPlusWidth <= availableWidth) {
          usedWidth += btnWidth;
          visibleCount++;
        } else {
          break;
        }
      }
      /**
       * setting the  hidden count.....
       */
      setHiddenCount(selectedFilters?.length - visibleCount);
    };

    // Initial measure
    calculateHiddenCount();

    const handleResize = () => {
      calculateHiddenCount();
    };

    /**
     * debouncing handle resize event as it trigger's on every subsequent re size.
     * function will be executed only after the user has stopped resizing for a 300ms delay
     */
    const debouncedResize = debounce(handleResize, 300);

    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [selectedFilters, popupOpenState]);

  const visibleCount = selectedFilters?.length - hiddenCount;

  /**
   * To plot the visible filter's out of the selected filters on UI.....
   */
  const visibleselectedFilters = sortedFilters?.slice(0, visibleCount);

  return (
    <StyledSelectedFiltersBar
      data-testid="luxury-SelectedFiltersBar"
      data-component-name="o-luxury-SelectedFiltersBar"
      ref={containerRef}
    >
      {/* ===========================================================
           HIDDEN ON UI JUST USED FOR THE CONTAINER CALCULATIONS....
        ============================================================ */}
      <div ref={pillsRef} aria-hidden="true" className="hidden-pills-container">
        {sortedFilters.map((f: any) => (
          <div className="mr-3 mb-3 px-2 pl-3 selected-filter-button" key={f?.id}>
            <span>{f?.label}</span>
            <span role="button" className="t-font-s">
              <Icon iconClass="icon-clear clear-button" />
            </span>
          </div>
        ))}
      </div>

      {/* ================================
          VISIBLE ON UI....
         ================================ */}

      {visibleselectedFilters?.map((selectedFilter: DisplayedFilter) => (
        <div className="mr-2 px-2 pl-3 selected-filter-button" key={selectedFilter?.id}>
          <span>{selectedFilter?.label}</span>
          <span
            tabIndex={0}
            role="button"
            aria-label={`Remove filter ${selectedFilter?.label}`}
            className="t-font-s"
            onClick={() => onRemoveFilter(selectedFilter?.type, selectedFilter?.code)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                // Prevent scrolling when Space is pressed
                e.preventDefault();
                onRemoveFilter(selectedFilter?.type, selectedFilter?.code);
              }
            }}
          >
            <Icon iconClass="icon-clear clear-button" />
          </span>
        </div>
      ))}
      {hiddenCount > 0 && <span className="t-label-xs" ref={hiddenCountRef}>{`+${hiddenCount}`}</span>}
    </StyledSelectedFiltersBar>
  );
};
